import { useEffect, useRef, useState } from 'react'
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

import type { LibraryDocument } from '../../api/library'
import { contentUrl } from '../../api/library'
import { Button } from '../../ui/Control'
import { Waiting } from '../../ui/Status'

export interface PdfPresentationProps {
  readonly document: LibraryDocument
  /** Persisted page, counted from one. */
  readonly page: number
  readonly onPage: (page: number) => void
}

/**
 * A PDF as slides: one page at a time, without the browser's PDF chrome.
 *
 * PDF.js is loaded only when such a card is actually on screen. The document stays open while pages
 * change, so turning a page renders from the bytes already held rather than downloading the PDF
 * again. Only the page number is persisted; zoom follows the size of the card.
 */
export function PdfPresentation({ document, page, onPage }: PdfPresentationProps) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const host = useRef<HTMLDivElement>(null)
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [failed, setFailed] = useState(false)
  const source = contentUrl(document)

  useEffect(() => {
    let disposed = false
    let loading: PDFDocumentLoadingTask | null = null

    void import('pdfjs-dist').then(async ({ GlobalWorkerOptions, getDocument }) => {
      GlobalWorkerOptions.workerSrc = pdfWorker
      try {
        loading = getDocument({ url: source, withCredentials: true })
        const opened = await loading.promise
        if (!disposed) {
          setPdf(opened)
          setFailed(false)
        }
      } catch {
        if (!disposed) {
          setFailed(true)
        }
      }
    })

    return () => {
      disposed = true
      void loading?.destroy()
    }
  }, [source])

  const shown = pdf === null ? page : Math.min(Math.max(1, page), pdf.numPages)

  useEffect(() => {
    if (pdf === null || shown === page) {
      return
    }
    // A replacement may have fewer pages than the version this card last showed.
    onPage(shown)
  }, [onPage, page, pdf, shown])

  useEffect(() => {
    const surface = canvas.current
    const box = host.current
    if (pdf === null || surface === null || box === null) {
      return
    }

    let render: RenderTask | null = null
    let disposed = false
    let generation = 0
    const draw = async () => {
      const thisDraw = ++generation
      render?.cancel()
      const pdfPage = await pdf.getPage(shown)
      if (disposed || thisDraw !== generation) {
        return
      }
      const natural = pdfPage.getViewport({ scale: 1 })
      const width = Math.max(1, box.clientWidth)
      const height = Math.max(1, box.clientHeight)
      const cssScale = Math.min(width / natural.width, height / natural.height)
      const pixelRatio = window.devicePixelRatio || 1
      const viewport = pdfPage.getViewport({ scale: cssScale * pixelRatio })
      surface.width = Math.ceil(viewport.width)
      surface.height = Math.ceil(viewport.height)
      surface.style.width = `${viewport.width / pixelRatio}px`
      surface.style.height = `${viewport.height / pixelRatio}px`
      const context = surface.getContext('2d')
      if (context !== null) {
        render = pdfPage.render({ canvasContext: context, viewport, canvas: surface })
        await render.promise
      }
    }

    void draw().catch((error: unknown) => {
      if (!disposed && !(error instanceof Error && error.name === 'RenderingCancelledException')) {
        setFailed(true)
      }
    })
    const observer =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => {
            void draw().catch(() => {
              if (!disposed) {
                setFailed(true)
              }
            })
          })
        : null
    observer?.observe(box)
    return () => {
      disposed = true
      observer?.disconnect()
      render?.cancel()
    }
  }, [pdf, shown])

  if (failed) {
    return <span className="thing__instead">PDF cannot be shown</span>
  }

  return (
    <div
      ref={host}
      className="thing__presentation"
      aria-label={`${document.title} — presentation`}
      onPointerDown={(event) => {
        event.stopPropagation()
      }}
      onDoubleClick={(event) => {
        event.stopPropagation()
      }}
    >
      {pdf === null ? <Waiting label="Opening the PDF." /> : <canvas ref={canvas} />}
      {pdf !== null && (
        <div className="thing__pageControls">
          <Button
            tone="floating"
            icon="back"
            iconOnlyLabel="Previous page"
            disabled={shown <= 1}
            onClick={() => {
              onPage(shown - 1)
            }}
          />
          <span aria-live="polite">
            {shown} / {pdf.numPages}
          </span>
          <Button
            tone="floating"
            icon="details"
            iconOnlyLabel="Next page"
            disabled={shown >= pdf.numPages}
            onClick={() => {
              onPage(shown + 1)
            }}
          />
        </div>
      )}
    </div>
  )
}
